/**
 * Dates on screen.
 *
 * Everything the API returns is an absolute instant in ISO form. It is shown
 * in Europe/London regardless of where the browser thinks it is, because the
 * lessons happen here and a tutor checking the diary from abroad wants the
 * time the student will turn up, not their own.
 */

const TZ = "Europe/London";

const time = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const day = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  weekday: "long",
  day: "numeric",
  month: "long",
});

const shortDay = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  day: "numeric",
  month: "short",
  year: "numeric",
});

const dayKeyFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const formatTime = (iso: string): string => time.format(new Date(iso));
export const formatDay = (iso: string): string => day.format(new Date(iso));
export const formatDate = (iso: string): string => shortDay.format(new Date(iso));

const timeWithSeconds = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
});
const zoneName = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, timeZoneName: "short" });

export function validTimestamp(iso: string | null | undefined): iso is string {
  return !!iso && Number.isFinite(Date.parse(iso));
}

/** A safe London timestamp for payment/import evidence, including the repeated autumn hour. */
export function formatDateTime(iso: string | null | undefined, seconds = false, withZone = false): string {
  if (!validTimestamp(iso)) return "Unknown";
  const clock = seconds ? timeWithSeconds.format(new Date(iso)) : formatTime(iso);
  const zone = withZone ? ` ${zoneName.formatToParts(new Date(iso)).find((part) => part.type === "timeZoneName")?.value ?? ""}` : "";
  return `${formatDate(iso)}, ${clock}${zone}`;
}

/** A stable YYYY-MM-DD for the London day an instant falls on. */
export const dayKey = (iso: string): string => dayKeyFormat.format(new Date(iso));

const ordinalDayMonth = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  day: "numeric",
  month: "long",
});

/**
 * The day a lesson fell on, written the way a person says it: "14th December".
 *
 * Read through `formatToParts` rather than by pulling a number off the Date,
 * because the day of the month in London is not always the day of the month
 * where the browser is -- a lesson at 00:30 on the 15th is still the 14th to
 * anyone an hour behind.
 *
 * No year. This names a lesson somebody is looking at, not a date in a record.
 */
export function formatOrdinalDate(iso: string): string {
  const parts = ordinalDayMonth.formatToParts(new Date(iso));
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  if (day === "" || month === "") return "";
  return `${day}${ordinalSuffix(Number(day))} ${month}`;
}

/** st, nd, rd, th -- with the eleven-to-thirteen exception English insists on. */
function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  const last = day % 10;
  if (last === 1) return "st";
  if (last === 2) return "nd";
  if (last === 3) return "rd";
  return "th";
}

/** The calendar day after a `YYYY-MM-DD` key. Read in UTC so no zone can shift it. */
export function nextDay(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** YYYY-MM-DD for a date input, offset by a number of days. */
export function isoDay(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return dayKeyFormat.format(date);
}

const wallClockOffsetFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Europe/London's UTC offset, in minutes, at a given instant. */
function londonOffsetMinutes(instant: number): number {
  const parts: Record<string, string> = {};
  for (const part of wallClockOffsetFormat.formatToParts(new Date(instant))) {
    if (part.type !== "literal") parts[part.type] = part.value;
  }
  // en-GB's 24-hour formatter can render midnight as "24" rather than "00"
  // in some engines; `% 24` folds it back before feeding it to Date.UTC.
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asUtc - instant) / 60_000);
}

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Why a wall clock might not name an instant.
 *
 * Three different problems, and a screen that offers to correct one of them
 * has to be able to tell them apart: "13-04-2026" is a typing mistake, and
 * 01:30 on the last Sunday in March is a time that does not happen. Telling
 * somebody the second is malformed sends them away to check a date that was
 * fine.
 */
export type LondonWallClockProblem = "malformed" | "gap" | "ambiguous";

export type LondonWallClockAttempt =
  | { ok: true; iso: string }
  | { ok: false; reason: LondonWallClockProblem };

/**
 * Convert a London wall-clock date/time into an absolute ISO instant.
 *
 * Europe/London only ever holds one of two UTC offsets: GMT (0 minutes) or
 * BST (60). The supplied wall clock is tried against both, and a candidate
 * only counts when trying it actually lands back in the offset it assumed —
 * everything else about the arithmetic follows from that one check.
 *
 * The half hour skipped every spring has no offset that lands on it: neither
 * candidate matches, and this refuses rather than silently landing on
 * whichever side the arithmetic happened to round to. The half hour repeated
 * every autumn has the opposite problem — both offsets land on it, genuinely
 * and correctly, and there is no reading of "09:15 on that day" that picks
 * one over the other. This refuses rather than choosing.
 *
 * The refusal is returned rather than thrown, because the caller that cares is
 * a form: somebody has typed a date and a time, and which of the three ways it
 * failed is the sentence they need to read. `londonWallClockIso` below is the
 * same answer for callers building a time they already know is valid.
 */
export function londonWallClockAttempt(date: string, time: string): LondonWallClockAttempt {
  const dateMatch = DATE_PATTERN.exec(date);
  const timeMatch = TIME_PATTERN.exec(time);
  if (!dateMatch || !timeMatch) return { ok: false, reason: "malformed" };

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const second = timeMatch[3] === undefined ? 0 : Number(timeMatch[3]);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return { ok: false, reason: "malformed" };
  }

  const wallClockMs = Date.UTC(year, month - 1, day, hour, minute, second);
  // `Date.UTC` never fails — it normalises an out-of-range day (31 April,
  // 30 February) into the day after, silently. Reading the components back
  // catches that rather than booking the wrong date.
  const rolled = new Date(wallClockMs);
  if (
    rolled.getUTCFullYear() !== year ||
    rolled.getUTCMonth() !== month - 1 ||
    rolled.getUTCDate() !== day
  ) {
    return { ok: false, reason: "malformed" };
  }

  // GMT (0) then BST (60): a candidate survives only when the offset it was
  // tried with is the offset Europe/London actually holds at the instant
  // that offset produces.
  const validOffsets = [0, 60].filter(
    (offsetMinutes) => londonOffsetMinutes(wallClockMs - offsetMinutes * 60_000) === offsetMinutes,
  );

  if (validOffsets.length === 0) return { ok: false, reason: "gap" };
  if (validOffsets.length > 1) return { ok: false, reason: "ambiguous" };

  return { ok: true, iso: new Date(wallClockMs - validOffsets[0]! * 60_000).toISOString() };
}

/** The same conversion, for a caller that treats an unreadable clock as a bug. */
export function londonWallClockIso(date: string, time: string): string {
  const attempt = londonWallClockAttempt(date, time);
  if (attempt.ok) return attempt.iso;
  if (attempt.reason === "gap") {
    throw new Error(`${date} ${time} does not exist in Europe/London (spring-forward gap)`);
  }
  if (attempt.reason === "ambiguous") {
    throw new Error(`${date} ${time} is ambiguous in Europe/London (repeated autumn hour)`);
  }
  throw new Error(`Invalid London wall-clock date/time: ${date} ${time}`);
}

/**
 * Titles for the calendar views.
 *
 * These take a day key rather than an instant. A day key is already a London
 * calendar date, so it is read back in UTC — converting it to an instant first
 * would push the 1st of a month into the 31st of the one before on any browser
 * running west of here, and rename the whole month with it.
 */
const monthOfYear = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  month: "long",
  year: "numeric",
});

const dayAndMonth = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "long",
});

const fromDayKey = (key: string): Date => new Date(`${key}T12:00:00.000Z`);

/** "August 2026", for the month view's heading. */
export const formatMonthOf = (key: string): string => monthOfYear.format(fromDayKey(key));

/**
 * "27 July 2026", for a control whose visible label is only a number.
 *
 * A month cell is labelled "27" and nothing else, which is all a sighted
 * reader needs and nothing like enough on its own — the day key underneath it
 * would be read out a character at a time.
 */
export const formatFullDay = (key: string): string =>
  `${dayAndMonth.format(fromDayKey(key))} ${key.slice(0, 4)}`;

const weekdayName = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long" });

/**
 * "Monday 27 July 2026", for a heading naming one particular day.
 *
 * The weekday is the part somebody actually navigates by — a diary is read as
 * "what does Thursday look like", not as "what does the 27th look like" — and
 * the date on its own makes you count. Every other view names its days: the
 * week heads its columns Mon to Sun, the month rows its weekdays, the list
 * writes them out. The day view was the one place a date stood alone.
 */
export const formatWeekdayDay = (key: string): string =>
  `${weekdayName.format(fromDayKey(key))} ${formatFullDay(key)}`;

/** The number a day is drawn with in a month cell. */
export const dayNumber = (key: string): string => String(Number(key.slice(8, 10)));

/**
 * "10 – 16 August 2026", and shorter where it can be.
 *
 * A week that straddles two months says both; one that does not says the month
 * once, because "10 August – 16 August 2026" is a heading that makes you read
 * it twice to learn nothing.
 */
export function formatWeekOf(from: string, to: string): string {
  const year = to.slice(0, 4);
  if (from.slice(0, 7) === to.slice(0, 7)) {
    return `${dayNumber(from)} – ${dayAndMonth.format(fromDayKey(to))} ${year}`;
  }
  const opening = dayAndMonth.format(fromDayKey(from));
  const closing = dayAndMonth.format(fromDayKey(to));
  // Only the closing year is shown when a week crosses New Year, since the
  // opening one is obvious from it.
  return `${opening} – ${closing} ${year}`;
}

/**
 * "4 minutes ago". Deliberately coarse — the point is whether the data is
 * current, and "3 minutes" and "4 minutes" mean the same thing to a reader.
 */
export function formatAgo(iso: string, now: number = Date.now()): string {
  const seconds = Math.round((now - Date.parse(iso)) / 1000);
  if (seconds < 0) return "just now";
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * A length of practice — one session, or a total of them.
 *
 * Seconds show whenever they are not zero, because a session can be measured
 * rather than typed: a timer that ran for a minute and seven seconds is not
 * "1m", and rounding it there makes the timer look like it is guessing, which
 * is the one thing it is not doing. A session typed as twenty minutes still
 * reads "20m" rather than "20m 0s".
 *
 * Above an hour they are dropped again, which is what lets one function serve
 * both jobs: a week's practice reads "3h 20m" because by then the minutes are
 * the story, while a first afternoon of testing reads "31m 7s" rather than
 * rounding to a figure that does not match the sessions it is a total of.
 */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole === 0) return "none yet";
  if (whole < 60) return `${whole}s`;

  if (whole < 3600) {
    const minutes = Math.floor(whole / 60);
    const rest = whole % 60;
    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  }

  const totalMinutes = Math.round(whole / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}
